import { resolveCallState } from 'Modules/softphone/resolver';
import { AudioService } from 'Modules/softphone/services/AudioService';
import { DebugService } from 'Modules/softphone/services/DebugService';
import { EventService } from 'Modules/softphone/services/EventService';
import { WebRTCService } from 'Modules/softphone/services/WebRTCService';
import { useSoftphoneStore } from 'Modules/softphone/store';
import type { CallSession } from 'Modules/softphone/types';
import { createLogger } from 'Modules/softphone/utils/logger';
import { SessionState as SipSessionState } from 'sip.js';
import type { SessionState } from 'sip.js';

const logger = createLogger('CallManager:SessionStateHandler');

/**
 * How each session state reads on the timeline.
 *
 * Every transition sip.js reports is recorded, not only the two the handler
 * acts on: a call that never leaves "establishing", or one that sits in
 * "terminating", is exactly the shape a stuck call has, and a timeline that
 * only shows the endpoints cannot show it.
 *
 * `Terminated` is absent on purpose — it is recorded by `handleCallTerminated`
 * with the context it has there, and would otherwise be said twice. So is
 * `Initial`, which is the state a session is created in rather than a change.
 */
const SESSION_STATE_MESSAGES: Partial<Record<SessionState, string>> = {
  [SipSessionState.Establishing]: 'Call establishing',
  [SipSessionState.Established]: 'Call established',
  [SipSessionState.Terminating]: 'Call terminating',
};

/**
 * Parameters for handling session state changes
 */
export interface SessionStateChangeParams {
  callId: string;
  state: SessionState;
  direction: 'inbound' | 'outbound';
  webrtcService: WebRTCService;
  audioService: AudioService;
  onScheduleCleanup: (callId: string) => void;
  onSwitchToHeldCall?: (callId: string) => void;
}

/**
 * Parameters for handling call established state
 */
interface CallEstablishedParams {
  callId: string;
  direction: 'inbound' | 'outbound';
  call: CallSession;
  webrtcService: WebRTCService;
  audioService: AudioService;
}

/**
 * Parameters for handling call terminated state
 */
interface CallTerminatedParams {
  callId: string;
  direction: 'inbound' | 'outbound';
  call: CallSession;
  audioService: AudioService;
  onSwitchToHeldCall?: (callId: string) => void;
}

export class SessionStateHandler {
  /**
   * Handle SIP session state changes
   */
  handleSessionStateChange(params: SessionStateChangeParams): void {
    const {
      callId,
      state,
      direction,
      webrtcService,
      audioService,
      onScheduleCleanup,
      onSwitchToHeldCall,
    } = params;

    const store = useSoftphoneStore.getState();
    const call = store.activeCalls.get(callId);

    if (!call?.session) {
      logger.warn('Session state change for unknown call:', { callId, state });
      return;
    }

    const stateMessage = SESSION_STATE_MESSAGES[state];
    if (stateMessage) {
      EventService.getInstance().logCallEvent(stateMessage, {
        callId,
        direction: call.direction,
        phoneNumber: call.phoneNumber,
        sessionState: String(state),
      });
    }

    switch (state) {
      case SipSessionState.Established:
        this.handleCallEstablished({
          callId,
          direction,
          call,
          webrtcService,
          audioService,
        });
        break;
      case SipSessionState.Terminated:
        this.handleCallTerminated({
          callId,
          direction,
          call,
          audioService,
          onSwitchToHeldCall,
        });

        // Don't schedule full cleanup for calls that belong to an active conference.
        // After REFER/BYE the original SIP session terminates, but the call should remain
        // in the store as a "virtual participant" of the conference. Audio resources are
        // already cleaned up in handleCallTerminated above.
        if (call.conferenceId && store.activeConference) {
          logger.info(
            'Skipping cleanup for conference participant (session terminated after REFER)',
            {
              callId,
              conferenceId: call.conferenceId,
            }
          );
        } else {
          onScheduleCleanup(callId);
        }
        break;
      default:
        logger.debug('Unhandled session state', { state, callId });
    }
  }

  /**
   * Handle call established state
   * Sets up peer connection and remote audio stream
   * Stops all ringtones/ringback tones
   */
  private handleCallEstablished(params: CallEstablishedParams): void {
    const { callId, direction, call, webrtcService, audioService } = params;
    if (!call.session) {
      logger.warn('No session found for established call', { callId });
      return;
    }

    logger.debug('Session established');
    logger.info('Call established');

    // Stop all call audio
    audioService.stopAllCallAudio();

    webrtcService.clearCachedStream(callId);

    // Only update state if call is not on hold
    // When a call is placed on hold or resumed, SIP.js sends a re-INVITE
    // which triggers an Established event. We must preserve the hold state.
    const store = useSoftphoneStore.getState();
    const currentCall = store.activeCalls.get(callId);
    const isCurrentlyOnHold = currentCall?.isOnHold ?? false;

    if (!isCurrentlyOnHold) {
      store.updateCall(callId, {
        state: resolveCallState({
          sessionState: SipSessionState.Established,
          direction,
          isOnHold: false, // Explicitly pass isOnHold to resolveCallState
        }),
        answerTime: new Date().toISOString(),
      });
    } else {
      // Even when preserving hold state, we should ensure the state is correctly set
      // This handles the case where a re-INVITE completes while call is on hold
      store.updateCall(callId, {
        state: resolveCallState({
          sessionState: SipSessionState.Established,
          direction,
          isOnHold: true, // Explicitly pass isOnHold to resolveCallState
        }),
      });
    }

    webrtcService.setupPeerConnection(call.session, callId);

    const remoteStream = webrtcService.getRemoteStream(callId);
    if (remoteStream) {
      logger.info('Setting remote stream in handleCallEstablished', {
        callId,
        trackCount: remoteStream.getTracks().length,
        isOnHold: isCurrentlyOnHold,
      });
      audioService.setRemoteStream(remoteStream);
    } else {
      logger.warn('No remote stream available in handleCallEstablished', {
        callId,
        isOnHold: isCurrentlyOnHold,
      });
    }
  }

  /**
   * Handle call terminated state
   * Captures debug info, updates store, and switches to held call if needed
   *
   * NOTE: Does NOT play callDisconnected tone to match legacy behavior.
   * Legacy only plays this tone on network failures (cancelCallOnNoWebRTC),
   * not on normal call termination.
   */
  private handleCallTerminated(params: CallTerminatedParams): void {
    const { callId, direction, call, audioService, onSwitchToHeldCall } =
      params;
    logger.debug('Session terminated');
    logger.info('Call terminated');

    // A terminated session is NOT evidence that a BYE arrived: a cancelled
    // outbound call, a rejection, or a local hangup all land here too, and some
    // of them put nothing on the wire at all. So this records the lifecycle
    // change only — the actual BYE/CANCEL/4xx is logged on the SIP channel by
    // the transport trace in SipService, where it really happened.
    EventService.getInstance().logCallEvent('Call terminated', {
      callId,
      phoneNumber: call.phoneNumber,
      direction: call.direction,
      endedBy: call.endedBy ?? null,
    });
    const store = useSoftphoneStore.getState();

    // When a conference participant's original SIP session terminates after REFER/BYE,
    // we should NOT update the call state to 'ended', disconnect audio, or switch calls.
    // The call remains as a "virtual participant" in the conference, and audio comes from
    // the conference room session, not the individual call sessions.
    const debugService = DebugService.getInstance();

    if (call.conferenceId && store.activeConference) {
      logger.info(
        'Skipping terminated state update for conference participant',
        {
          callId,
          conferenceId: call.conferenceId,
        }
      );

      // The state update is skipped on purpose — the call stays a virtual
      // participant and its audio comes from the room session. The diagnostic
      // record is NOT optional though: without it a conference leg left no
      // history at all. Capture, then keep the early return intact.
      debugService.capture(callId, {
        phoneNumber: call.phoneNumber,
        direction: call.direction,
        state: 'ended',
        startTime: call.startTime,
        answerTime: call.answerTime,
        endTime: new Date().toISOString(),
        scenario: 'conference',
        endedBy: call.endedBy ?? null,
        interactionId: call.conferenceId,
        relatedCallIds: store.activeConference.participantIds.filter(
          (id) => id !== callId
        ),
        conference: {
          roomGuid: call.conferenceId,
          participantIds: [...store.activeConference.participantIds],
          participantsAreLocallyInferred: true,
        },
      });
      return;
    }

    debugService.capture(callId, {
      phoneNumber: call.phoneNumber,
      direction: call.direction,
      state: 'ended',
      startTime: call.startTime,
      answerTime: call.answerTime,
      endTime: new Date().toISOString(),
      endedBy: call.endedBy ?? null,
    });

    store.updateCall(callId, {
      state: resolveCallState({
        sessionState: SipSessionState.Terminated,
        direction,
      }),
      endTime: new Date().toISOString(),
    });

    // Disconnect remote audio
    audioService.setRemoteStream(null);

    // Switch to longest held call if needed (delegated to CallManager)
    if (onSwitchToHeldCall) {
      onSwitchToHeldCall(callId);
    }

    // Do NOT play callDisconnected tone here - legacy only plays it on network failures
    // void audioService.play('callDisconnected');
  }
}
